import { useCallback, useReducer, useEffect } from 'react';
import { Variable } from '@restworld/utility-types';
import { useGlobalsService } from '@restworld/data-services';

enum VariableTypes {
  LOADING = 'LOADING',
  SET_VARIABLE_OPTIONS = 'SET_VARIABLE_OPTIONS',
  SET_PENDING_VARIABLE_OPTIONS = 'SET_PENDING_VARIABLE_OPTIONS',
}
export const useVariableOptions = () => {
  const globalService = useGlobalsService();
  const [variableOptionsState, dispatch] = useReducer(variableOptionReducer, {
    loading: false,
    variableOptions: {},
    pendingVariableNames: [],
  });
  const { variableOptions, loading, pendingVariableNames } = variableOptionsState;
  const fetchVariable = useCallback(
    (variableName: string[]) => {
      let needUpdate = false;
      variableName.forEach((v) => {
        if (!pendingVariableNames?.includes(v)) needUpdate = true;
      });
      if (!needUpdate) return;
      const pendingVariableNamesCp = pendingVariableNames
        ? [...pendingVariableNames, ...variableName]
        : [...variableName].filter((variable, index, self) => self.findIndex((v) => v === variable) === index).sort();
      dispatch({
        type: VariableTypes.SET_PENDING_VARIABLE_OPTIONS,
        payload: {
          pendingVariableNames: pendingVariableNamesCp,
        },
      });
    },
    [dispatch, pendingVariableNames]
  );
  const fetchVariableOptions = useCallback(
    (variableNames: string[]) => {
      dispatch({ type: VariableTypes.LOADING, payload: { loading: true } });
      Promise.all(variableNames.map((variableName) => globalService.fetchVariableOptions(variableName)))
        .then((res) => {
          const newVars: VariableOptionType = {};
          res.forEach((r, index) => {
            newVars[variableNames[index]] = r.data;
          });
          dispatch({
            type: VariableTypes.SET_VARIABLE_OPTIONS,
            payload: { variableOptions: { ...variableOptions, ...newVars } },
          });
        })
        .catch((err) => {
          console.error(err, 'error fetching options');
          return err;
        })
        .finally(() => {
          dispatch({ type: VariableTypes.LOADING, payload: { loading: false } });
        });
    },
    [dispatch, globalService, variableOptions]
  );
  useEffect(() => {
    if (variableOptions && pendingVariableNames) {
      const useVariableOptions: string[] = pendingVariableNames.filter((v) => !(v in variableOptions));
      if (useVariableOptions.length) fetchVariableOptions(useVariableOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVariableNames]);
  return {
    loading,
    fetchVariable,
    variableOptions,
  };
};
// --> reducer
const variableOptionReducer = (state: VariableState, action: VariableOptionAction) => {
  const { type, payload } = action;
  switch (type) {
    case VariableTypes.LOADING:
      return {
        ...state,
        loading: payload.loading,
      };
    case VariableTypes.SET_VARIABLE_OPTIONS:
      return {
        ...state,
        variableOptions: payload.variableOptions,
      };
    case VariableTypes.SET_PENDING_VARIABLE_OPTIONS:
      return {
        ...state,
        pendingVariableNames: payload.pendingVariableNames,
      };
    default:
      return { ...state };
  }
};
interface VariableOptionAction {
  type: VariableTypes;
  payload: VariableState;
}
interface VariableState {
  loading?: boolean;
  variableOptions?: VariableOptionType;
  pendingVariableNames?: string[];
}
type VariableOptionType = {
  [variableName: string]: Variable[];
};
